
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useMutation, useApolloClient } from '@apollo/client';
import { LOGIN_USER, REGISTER_USER } from '../graphql/queries';
import { jwtDecode } from 'jwt-decode';
import { User, AuthContextType } from '../types';

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const client = useApolloClient();

    const [loginMutation] = useMutation(LOGIN_USER);
    const [registerMutation] = useMutation(REGISTER_USER);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decoded = jwtDecode<User & { exp: number }>(token);
                if (decoded.exp * 1000 < Date.now()) {
                    logout();
                } else {
                    setUser(decoded);
                }
            } catch (err) {
                logout();
            }
        }
        setLoading(false);
    }, []);

    const login = async (email: string, password: string): Promise<User> => {
        try {
            const { data } = await loginMutation({
                variables: { email, password }
            });
            const { token, user } = data.login;
            localStorage.setItem('token', token);
            setUser(user);
            return user;
        } catch (error) {
            throw error;
        }
    };

    const register = async (name: string, email: string, password: string): Promise<User> => {
        try {
            const { data } = await registerMutation({
                variables: { name, email, password }
            });
            const { token, user } = data.register;
            localStorage.setItem('token', token);
            setUser(user);
            return user;
        } catch (error) {
            throw error;
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        setUser(null);
        client.clearStore().catch(err => console.error(err));
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
